/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box } from '@material-ui/core';
import { assetCdnUrl } from 'config';
import useStyles from './ApplicationCard.styles';

const CDN_URL = `${assetCdnUrl}connector-icons/v1/`;

const applicationIcons: Record<string, string> = {
  Salesforce: `${CDN_URL}Salesforce.svg`,
  'Google Sheets': `${CDN_URL}GoogleSheets.svg`,
  'Google Chat': `${CDN_URL}GoogleChat.svg`,
  'Google Forms': `${CDN_URL}GoogleForms.svg`,
  'Google Drive': `${CDN_URL}GoogleDrive.svg`,
  Gmail: `${CDN_URL}Gmail.svg`,
  GitHub: `${CDN_URL}GitHub.svg`,
  QuickBooks: `${CDN_URL}QuickBooks.svg`,
  Stripe: `${CDN_URL}Stripe.svg`,
  HubSpot: `${CDN_URL}HubSpot.svg`,
  Mailchimp: `${CDN_URL}Mailchimp.svg`,
  BambooHR: `${CDN_URL}BambooHR.svg`,
  Slack: `${CDN_URL}Slack.svg`,
  Shopify: `${CDN_URL}Shopify.svg`,
  Xero: `${CDN_URL}Xero.svg`,
  Zendesk: `${CDN_URL}Zendesk.svg`,
  Airtable: `${CDN_URL}Airtable.svg`,
  LinkedIn: `${CDN_URL}LinkedIn.svg`,
  Workday: `${CDN_URL}Workday.svg`,
  'Active Directory': `${CDN_URL}ActiveDirectory.svg`,
  PayPal: `${CDN_URL}Paypal.svg`,
  OpenAI: `${CDN_URL}OpenAI.svg`,
  'AWS S3': `${CDN_URL}AWS-S3.svg`,
  PostgreSQL: `${CDN_URL}PostgreSQL.svg`,
  WooCommerce: `${CDN_URL}WooCommerce.svg`,
  ADP: `${CDN_URL}ADP.svg`,
  Twilio: `${CDN_URL}Twilio.svg`,
  Twitter: `${CDN_URL}Twitter.svg`,
  Jira: `${CDN_URL}Jira.svg`,
  NetSuite: `${CDN_URL}NetSuite.svg`,
};

const DEFAULT_APPLICATION_ICON = '/images/develop-primary.svg';

export const getApplicationIcon = (name: string): string =>
  applicationIcons[name] ?? DEFAULT_APPLICATION_ICON;

interface ApplicationIconProps {
  application?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function ApplicationIcon({
  application,
  size = 'lg',
}: ApplicationIconProps) {
  const classes = useStyles();
  let imageSizeClass;
  switch (size) {
    case 'sm':
      imageSizeClass = classes.cardImgContainerSm;
      break;
    case 'md':
      imageSizeClass = classes.cardImgContainerMd;
      break;
    default:
      imageSizeClass = classes.cardImgContainer;
      break;
  }
  return (
    <Box className={imageSizeClass}>
      {application ? (
        <img
          src={getApplicationIcon(application)}
          alt={application}
          className={classes.cardImg}
        />
      ) : (
        <Box className={classes.cardImg} />
      )}
    </Box>
  );
}

export default ApplicationIcon;
